/**
 * Espace documentaire transversal : recherche et filtre sur l'ensemble des documents de la flotte
 * (`GET /v1/documents`, D58), sans partir d'un numéro de GRV — l'onglet Documents d'une fiche GRV
 * (`OngletDocuments.tsx`) reste l'écran dédié à la consultation et au dépôt pour un GRV précis.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { ouvrirTelechargement } from "../../shared/composants/telechargement";
import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import { EnTeteTriable, useTrie, useTriTableau } from "../../shared/composants/tableauTriable";
import { formaterDateSeule } from "../../shared/composants/formatage";
import { BoutonExporterCsv } from "../../shared/composants/exportCsv";
import type { Document } from "../../shared/api/types";
import { useDocumentsTransversal, useUrlDeTelechargement } from "./requetes";

type Colonne = "numero_grv" | "type_document" | "date_fichier" | "date_fin_validite";

function valeurDe(document: Document, colonne: Colonne): string | null | undefined {
  return document[colonne];
}

export function EcranDocuments() {
  const [numeroGrv, definirNumeroGrv] = useState("");
  const [typeDocument, definirTypeDocument] = useState("");
  const telecharger = useUrlDeTelechargement();
  const { tri, basculerTri } = useTriTableau<Colonne>({ colonne: "date_fichier", descendant: true });

  const requete = useDocumentsTransversal({
    numeroGrv: numeroGrv || undefined,
    typeDocument: typeDocument || undefined,
  });

  const documents = useMemo(() => requete.data?.data ?? [], [requete.data]);
  const triees = useTrie(documents, tri, valeurDe);
  const typesPresents = useMemo(
    () => Array.from(new Set(documents.map((d) => d.type_document))).sort(),
    [documents],
  );

  function ouvrirLeTelechargement(documentId: string): void {
    ouvrirTelechargement(
      async () => (await telecharger.mutateAsync(documentId)).data.url_telechargement,
    );
  }

  return (
    <section>
      <h1>Documents</h1>
      <p className="carte__description">
        Vue transversale sur l'ensemble de la flotte. Pour déposer un document, ouvrir la fiche du
        GRV concerné, onglet « Documents ».
      </p>

      <div className="barre-de-filtres">
        <label htmlFor="filtre-doc-grv">Numéro de GRV</label>
        <input
          id="filtre-doc-grv"
          type="search"
          placeholder="ex. GRV-00001"
          value={numeroGrv}
          onChange={(e) => definirNumeroGrv(e.target.value)}
        />

        <label htmlFor="filtre-doc-type">Type</label>
        <select
          id="filtre-doc-type"
          value={typeDocument}
          onChange={(e) => definirTypeDocument(e.target.value)}
        >
          <option value="">Tous les types</option>
          {typesPresents.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        <BoutonExporterCsv
          nomFichier="documents"
          colonnes={[
            { libelle: "GRV", valeur: (d: Document) => d.numero_grv },
            { libelle: "Type", valeur: (d: Document) => d.type_document },
            { libelle: "Date du fichier", valeur: (d: Document) => formaterDateSeule(d.date_fichier) },
            {
              libelle: "Fin de validité",
              valeur: (d: Document) =>
                d.date_fin_validite ? formaterDateSeule(d.date_fin_validite) : "",
            },
          ]}
          lignes={triees}
        />
      </div>

      {requete.isPending && <Chargement libelle="Chargement des documents…" />}
      {requete.isError && <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />}
      {requete.data && triees.length === 0 && (
        <Vide libelle="Aucun document ne correspond à ces filtres." />
      )}

      {triees.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <EnTeteTriable colonne="numero_grv" libelle="GRV" tri={tri} basculerTri={basculerTri} />
              <EnTeteTriable
                colonne="type_document"
                libelle="Type"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="date_fichier"
                libelle="Date du fichier"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="date_fin_validite"
                libelle="Fin de validité"
                tri={tri}
                basculerTri={basculerTri}
              />
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {triees.map((document) => (
              <tr key={document.document_id}>
                <td>
                  <Link to={`/grv/${encodeURIComponent(document.numero_grv)}/documents`}>
                    {document.numero_grv}
                  </Link>
                </td>
                <td>{document.type_document}</td>
                <td>{formaterDateSeule(document.date_fichier)}</td>
                <td>{document.date_fin_validite ? formaterDateSeule(document.date_fin_validite) : "—"}</td>
                <td>
                  <button type="button" onClick={() => ouvrirLeTelechargement(document.document_id)}>
                    Télécharger
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
